import { router } from '@inertiajs/react'

import { Avatar } from '../../../utils/avatar'

function StatusTag({ user }) {
  const role = user?.role
  if (role === 'super_admin') return <span className="badge text-bg-warning-subtle ms-2">Super Admin</span>
  if (!user?.supervisor_id) return <span className="badge text-bg-secondary-subtle ms-2">No supervisor</span>
  return null
}

function buildTree(nodes) {
  const byId = new Map(nodes.map((n) => [String(n.id), { ...n, children: [] }]))
  const roots = []

  byId.forEach((n) => {
    const parentId = n.supervisor_id ? String(n.supervisor_id) : null
    if (parentId && byId.has(parentId)) {
      byId.get(parentId).children.push(n)
    } else {
      roots.push(n)
    }
  })

  const sortFn = (a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '')
  const sortRec = (node) => {
    node.children.sort(sortFn)
    node.children.forEach(sortRec)
  }
  roots.sort(sortFn)
  roots.forEach(sortRec)

  return roots
}

function TreeNode({ node, depth = 0 }) {
  return (
    <div style={{ marginLeft: depth * 16 }}>
      <div className="d-flex align-items-center gap-2 py-1">
        <Avatar user={node} size="xs" />
        <span className="fw-semibold">{node.full_name || node.email}</span>
        <span className="text-muted small">{node.email}</span>
        <StatusTag user={node} />
      </div>
      {node.children?.length ? node.children.map((c) => <TreeNode key={c.id} node={c} depth={depth + 1} />) : null}
    </div>
  )
}

export default function Index({ managers, super_admins, unassigned_users, tree }) {
  const roots = buildTree(tree?.nodes || [])

  return (
    <div className="container-fluid">
      <div className="header">
        <div className="header-body">
          <div className="row align-items-center">
            <div className="col">
              <h6 className="header-pretitle">Human Resources</h6>
              <h1 className="header-title text-truncate">Org Chart</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-xl-8">
          <div className="card">
            <div className="card-header">
              <h4 className="card-header-title">Managers</h4>
            </div>

            <div className="table-responsive">
              <table className="table table-sm table-hover table-nowrap card-table">
                <thead>
                  <tr>
                    <th>Manager</th>
                    <th>Email</th>
                    <th className="text-end">Direct reports</th>
                  </tr>
                </thead>
                <tbody className="list">
                  {(managers || []).map((m) => (
                    <tr key={m.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <Avatar user={m} size="xs" className="me-3" />
                          <div>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-reset"
                              onClick={() => router.visit(`/company_settings/org_chart/${m.id}`)}
                            >
                              {m.full_name || m.email}
                            </button>
                            <StatusTag user={m} />
                          </div>
                        </div>
                      </td>
                      <td className="text-body-secondary">{m.email}</td>
                      <td className="text-end">
                        <span className="badge text-bg-secondary-subtle">{m.direct_reports_count || 0}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-4">
          <div className="card mb-3">
            <div className="card-header">
              <h4 className="card-header-title">Super Admins</h4>
            </div>
            <div className="card-body">
              {(super_admins || []).length ? (
                <div className="d-flex flex-column gap-2">
                  {super_admins.map((u) => (
                    <div key={u.id} className="d-flex align-items-center gap-2">
                      <Avatar user={u} />
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{u.full_name || u.email}</div>
                        <div className="small text-muted">{u.email}</div>
                      </div>
                      <StatusTag user={u} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted">None</div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h4 className="card-header-title">Users without supervisors</h4>
            </div>
            <div className="card-body">
              {(unassigned_users || []).length ? (
                <div className="d-flex flex-column gap-2">
                  {unassigned_users.map((u) => (
                    <div key={u.id} className="d-flex align-items-center gap-2">
                      <Avatar user={u} />
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{u.full_name || u.email}</div>
                        <div className="small text-muted">{u.email}</div>
                      </div>
                      <StatusTag user={u} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted">None</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-header-title">Org chart</h4>
            </div>
            <div className="card-body">
              {roots.map((r) => (
                <TreeNode key={r.id} node={r} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
